import { create } from 'zustand'

export interface EnergyFlowState {
  // Pulse shape
  baseGlow: number
  pulseAmplitude: number
  pulseCount: number
  edgeSharpness: number
  tailFalloff: number
  coreWidth: number

  // Color remap — charge state (ess_active_power > 0)
  colorACharge: string
  colorBCharge: string

  // Color remap — discharge state (ess_active_power < 0)
  colorADischarge: string
  colorBDischarge: string

  // Color remap — idle state (no significant SOC change)
  colorAIdle: string
  colorBIdle: string

  // Emission
  intensity: number

  // Transparency
  transparent: boolean
  opacity: number

  // Animation
  scrollSpeed: number
  minScrollSpeed: number
  colorLerpSpeed: number

  // Persistence
  isDirty: boolean
  isSaving: boolean

  update: (partial: Partial<Omit<EnergyFlowState, 'update' | 'isDirty' | 'isSaving' | 'saveToServer' | 'loadFromServer'>>) => void
  saveToServer: () => Promise<void>
  loadFromServer: () => Promise<void>
}

// Keys that are persisted to the server (excludes actions and flags)
const settingsKeys = [
  'baseGlow', 'pulseAmplitude', 'pulseCount',
  'edgeSharpness', 'tailFalloff', 'coreWidth',
  'colorACharge', 'colorBCharge',
  'colorADischarge', 'colorBDischarge',
  'colorAIdle', 'colorBIdle',
  'intensity', 'transparent', 'opacity',
  'scrollSpeed', 'minScrollSpeed', 'colorLerpSpeed',
] as const

export const useEnergyFlowStore = create<EnergyFlowState>((set) => ({
  // Pulse shape
  baseGlow: 0.15,
  pulseAmplitude: 2.0,
  pulseCount: 3.0,
  edgeSharpness: 10.0,
  tailFalloff: 4.0,
  coreWidth: 0.5,

  // Color remap — charge
  colorACharge: '#00ff4c',
  colorBCharge: '#00ff9d',

  // Color remap — discharge
  colorADischarge: '#0055ff',
  colorBDischarge: '#00e1ff',

  // Color remap — idle
  colorAIdle: '#cccccc',
  colorBIdle: '#ffffff',

  // Emission
  intensity: 1.5,

  // Transparency
  transparent: false,
  opacity: 1.0,

  // Animation
  scrollSpeed: 0.5,
  minScrollSpeed: 0.05,
  colorLerpSpeed: 3.0,

  // Persistence
  isDirty: false,
  isSaving: false,

  update: (partial) => set({ ...partial, isDirty: true }),

  saveToServer: async () => {
    set({ isSaving: true })
    console.log('[EnergyFlowStore] Save disabled in static mode')
    set({ isDirty: false, isSaving: false })
  },

  loadFromServer: async () => {
    try {
      const res = await fetch('/data/energy-flow-config.json')
      if (res.ok) {
        const data = await res.json()
        if (data && Object.keys(data).length > 0) {
          const patch: Record<string, unknown> = {}
          for (const key of settingsKeys) {
            if (key in data) {
              patch[key] = data[key]
            }
          }
          set({ ...patch, isDirty: false } as Partial<EnergyFlowState>)
        }
      }
    } catch {
      console.log('[EnergyFlowStore] Config not available, using defaults')
    }
  },
}))
